// AdditionalSumsEditor.tsx
import React from 'react'
import { observer } from 'mobx-react'
import { Dialog, Classes, InputGroup } from "@blueprintjs/core"
import { Field, FieldArray, Form, Formik } from 'formik'
import { v4 as uuidv4 } from 'uuid'
import { MoneyInput } from "modules/common/components/money/MoneyInput"
import { PlanrButton } from 'modules/common/components/planr/button/Button'
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { Buttons } from "modules/common/components/form"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"
import styles from "./AdditionalSumsEditor.module.scss"

interface AdditionalSum {
  guid: string
  name: string
  sum: number
}

interface AdditionalSumsEditorProps {
  isOpen: boolean
  onClose: () => void
  additionalSums: AdditionalSum[]
  onSave: (sums: AdditionalSum[]) => void
}

export const AdditionalSumsEditor: React.FC<AdditionalSumsEditorProps> = observer(({
  isOpen,
  onClose,
  additionalSums,
  onSave
}) => {
  const handleSubmit = (values: { sums: AdditionalSum[] }) => {
    onSave(values.sums.filter((s) => s.name.trim() || s.sum !== 0))
    onClose()
  }

  return (
    <Dialog
      title="Дополнительные выплаты"
      isOpen={isOpen}
      onClose={onClose}
      backdropClassName="standard"
      className={styles.dialog}
    >
      <Formik
        initialValues={{ sums: additionalSums.length ? additionalSums : [] }}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div className={Classes.DIALOG_BODY}>
              <div className={styles.editorContainer}>
                <FieldArray name="sums">
                  {({ push, remove }) => (
                    <>
                      <div className={styles.header}>
                        <div className={styles.nameColumn}>Наименование</div>
                        <div className={styles.sumColumn}>Сумма</div>
                        <div className={styles.actionsColumn}></div>
                      </div>

                      <div className={styles.rows}>
                        {values.sums.map((sum, index) => (
                          <div key={sum.guid} className={styles.row}>
                            <div className={styles.nameColumn}>
                              <Field name={`sums.${index}.name`}>
                                {({ field }: any) => (

                                  <InputGroup
                                    id={field.name}
                                    {...field}
                                    className="planr-default-input"
                                    autoComplete="off"
                                    data-lpignore="true"
                                    type="text"
                                    placeholder="Наименование"
                                  />
                                )}
                              </Field>
                            </div>
                            <div className={styles.sumColumn}>
                              <Field name={`sums.${index}.sum`}>
                                {({ field }: any) => (
                                  <MoneyInput
                                    className="planr-default-input"
                                    value={field.value}
                                    onMoneyChange={(money) => {
                                      setFieldValue(`sums.${index}.sum`, money.value)
                                    }}
                                    small={false}
                                    autoComplete="off"
                                  />
                                )}
                              </Field>
                            </div>
                            <div className={styles.actionsColumn}>
                              <RemoveConfirmation
                                onConfirmed={() => remove(index)}
                                what={() => "дополнительную сумму"}
                              >
                                {({ confirmRemoving }) => (
                                  <GeneralIcon
                                    type="general-trash"
                                    onClick={confirmRemoving}
                                    title="Удалить"
                                    style={{ cursor: "pointer", color: "#E31818" }}
                                  />
                                )}
                              </RemoveConfirmation>
                            </div>
                          </div>
                        ))}
                      </div>

                      {values.sums.length === 0 && (
                        <div className={styles.emptyState}>
                          <GeneralIcon type="general-info" />
                          <span>Нет дополнительных сумм</span>
                        </div>
                      )}

                      <div className={styles.addButton}>
                        <PlanrButton
                          type="lightblueish"
                          size="small"
                          onClick={() => push({ guid: uuidv4(), name: "", sum: 0 })}
                        >
                          <GeneralIcon type="general-plus" />
                          Добавить сумму
                        </PlanrButton>
                      </div>
                    </>
                  )}
                </FieldArray>
              </div>
            </div>

            <div className={Classes.DIALOG_FOOTER}>
              <Buttons
                left={
                  <div className={styles.dialogButtons}>
                    <PlanrButton size="small" type="graybtn" onClick={onClose}>
                      Отмена
                    </PlanrButton>
                    <PlanrButton size="small" type="greenish" nativeType="submit">
                      Сохранить
                    </PlanrButton>
                  </div>
                }
              />
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
})